import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles, professionalCategories, serviceCategories } from '@afficheservices/db/schema'
import { eq, sql, and } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, FAQSchema, CityPlaceSchema } from '~/components/structured-data'
import { CityMap } from '~/components/city-map'
import { CrossLinksHub } from '~/components/cross-links'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { PROBLEMES } from '~/lib/problemes-data.server'
import { URGENCY_LABELS } from '~/lib/problemes-types'
import { INTROS_BY_PROBLEM } from '~/lib/problemes-intros'
import { SERVICE_LABELS } from '~/lib/programmatic-seo'
import {
  H1_VARIANTS_PROBLEM,
  pickVariant,
  CLIMATE_NOTES,
  REGULATIONS_BY_DEPT,
  DEFAULT_REGULATION_NOTE,
  DEPT_FACTS,
  DEFAULT_DEPT_FACT,
  formatFr,
  formatResponseTime,
} from '~/lib/page-variants'
import { getNeighborCities } from '~/lib/city-data.server'
import { fetchCityServiceStats } from '~/lib/city-service-stats.server'
import { wikidataUrlForInsee, communeImageForInsee } from '~/lib/commune-wikidata.server'
import { isPathIndexable } from '~/lib/index-whitelist.server'
import { getProgrammaticContent } from '~/lib/programmatic-content.server'
import {
  ArrowLeft, ArrowRight, CheckCircle, XCircle, Euro, Phone, MapPin, Users,
} from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const problem = PROBLEMES.find((p) => p.slug === params.slug)
  if (!problem) throw new Response('Not Found', { status: 404 })

  const citySlug = params.city ?? ''
  // Resolve city from geo table — match slug-ified name + grab population
  // for content variation across pages.
  const cityRows = await db
    .select({
      name: geoFrance.communeName,
      postalCode: geoFrance.postalCode,
      dept: geoFrance.departmentCode,
      deptName: geoFrance.departmentName,
      region: geoFrance.regionName,
      population: geoFrance.population,
      lat: geoFrance.latitude,
      lon: geoFrance.longitude,
      communeCode: geoFrance.communeCode,
    })
    .from(geoFrance)
    .where(sql`LOWER(REPLACE(REPLACE(
      TRANSLATE(${geoFrance.communeName},
        'àáâãäåèéêëìíîïòóôõöùúûüýÿñçÀÁÂÃÄÅÈÉÊËÌÍÎÏÒÓÔÕÖÙÚÛÜÝŸÑÇ',
        'aaaaaaeeeeiiiioooooouuuuyyncAAAAAAEEEEIIIIOOOOOUUUUYYNC'),
      ' ', '-'), '''', '-')) = ${citySlug}`)
    .limit(1)

  if (cityRows.length === 0) throw new Response('Ville non trouvée', { status: 404 })
  const city = cityRows[0]
  // 410 only if every namesake is DOM-TOM (keeps mainland Saint-Denis etc.).
  await assertCityNotDomTom(citySlug)

  // Pro count + top 3 pros in this department for the relevant categories.
  // These names + count make every city page diverge — critical for
  // duplicate-content avoidance across 121k programmatic URLs.
  const catSlugs = problem.serviceSlugs
  let proCount = 0
  let topPros: Array<{ id: string; companyName: string; slug: string; city: string; ratingAvg: string | null; reviewCount: number }> = []

  if (catSlugs.length > 0) {
    const [row] = await db
      .select({ count: sql<number>`count(DISTINCT ${professionalProfiles.id})::int` })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .innerJoin(serviceCategories, eq(serviceCategories.id, professionalCategories.categoryId))
      .where(and(
        eq(professionalProfiles.departmentCode, city.dept),
        eq(professionalProfiles.isDemo, false),
        sql`${serviceCategories.slug} IN (${sql.join(catSlugs.map(s => sql`${s}`), sql`, `)})`,
      ))
    proCount = Number(row?.count ?? 0)

    if (proCount > 0) {
      topPros = await db
        .select({
          id: professionalProfiles.id,
          companyName: professionalProfiles.companyName,
          slug: professionalProfiles.slug,
          city: professionalProfiles.city,
          ratingAvg: professionalProfiles.ratingAvg,
          reviewCount: professionalProfiles.reviewCount,
        })
        .from(professionalProfiles)
        .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
        .innerJoin(serviceCategories, eq(serviceCategories.id, professionalCategories.categoryId))
        .where(and(
          eq(professionalProfiles.departmentCode, city.dept),
          sql`${serviceCategories.slug} IN (${sql.join(catSlugs.map(s => sql`${s}`), sql`, `)})`,
        ))
        .orderBy(sql`${professionalProfiles.reviewCount} DESC NULLS LAST, ${professionalProfiles.companyName}`)
        .limit(3)
    }
  }

  // Per-city signals for content uniqueness — neighbors + live activity.
  // Both queries are bounded and indexed; ~30ms total.
  const lat = Number(city.lat)
  const lon = Number(city.lon)
  const [neighbors, stats] = await Promise.all([
    getNeighborCities(lat, lon, city.communeCode, 5, 30),
    catSlugs.length > 0
      ? (async () => {
          // Resolve first matching category id once for stats lookup.
          const [catRow] = await db
            .select({ id: serviceCategories.id })
            .from(serviceCategories)
            .where(eq(serviceCategories.slug, catSlugs[0]))
            .limit(1)
          return catRow ? fetchCityServiceStats(catRow.id, city.name) : null
        })()
      : Promise.resolve(null),
  ])

  return {
    problem, city, proCount, topPros, citySlug, neighbors, stats,
    // index only if Google already ranks this exact URL (GSC whitelist);
    // the never-ranked tail stays noindex. See index-whitelist.server.ts.
    indexable: await isPathIndexable(`/problemes/${problem.slug}/${citySlug}`),
    // Phase-2 per-city AI override (demand-head only); null → template.
    aiContent: await getProgrammaticContent({ routeType: 'problemes', slug: problem.slug, citySlug }),
    // Resolve the per-city intro variant server-side so the ~436-line
    // INTROS_BY_PROBLEM map stays out of the client bundle.
    introText: INTROS_BY_PROBLEM[problem.slug]?.[pickVariant(citySlug, 5)] ?? problem.description,
    wikidataUrl: wikidataUrlForInsee(city.communeCode),
    communeImageUrl: communeImageForInsee(city.communeCode),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const { problem: p, city } = data
  // Title decoupled from the rotated H1 (which can top 60 chars on long
  // problem+city combos, e.g. the "…: que faire et qui appeler" variant).
  // Short question form, capped at 60 so the SERP never truncates (DataForSEO:
  // title_too_long); the visible <h1> below stays long and descriptive.
  const shortTitle = `${p.title} à ${city.name} : que faire ?`
  const title = data.aiContent?.metaTitle ?? (shortTitle.length <= 60 ? shortTitle : `${p.title} à ${city.name}`)
  const rawDesc = data.aiContent?.metaDescription ?? `${p.title} à ${city.name} (${city.postalCode}) : ${p.shortDesc} Coût moyen ${p.avgCost}.`
  // Cap at 160 on a word boundary (DataForSEO: description_length > 165 truncates).
  const desc = rawDesc.length <= 160 ? rawDesc : rawDesc.slice(0, 157).replace(/\s+\S*$/, '') + '…'
  return [
    { title },
    { name: 'description', content: desc },
    // Conditional index: GSC-proven URLs (Google already ranks them) index;
    // the never-ranked tail stays noindex,follow. Whitelist refreshed weekly
    // from Search Console — see index-whitelist.server.ts.
    { name: 'robots', content: data.indexable ? 'index, follow' : 'noindex, follow' },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/problemes/${p.slug}/${data.citySlug}` },
    { property: 'og:title', content: title },
    { property: 'og:description', content: desc },
    { property: 'og:url', content: `https://www.afficheservices.fr/problemes/${p.slug}/${data.citySlug}` },
    { property: 'og:image', content: `https://www.afficheservices.fr/og/problemes/${p.slug}/${data.citySlug}.svg` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:type', content: 'image/svg+xml' },
  ]
}

export default function ProblemeCityPage() {
  const { problem: p, city, proCount, topPros, citySlug, neighbors, stats, introText, wikidataUrl, communeImageUrl, aiContent } = useLoaderData<typeof loader>()
  // Hash-based H1 rotation — same city always gets same variant (stable
  // for SEO crawls); different cities spread across 5 templates.
  const h1 = H1_VARIANTS_PROBLEM[pickVariant(citySlug, H1_VARIANTS_PROBLEM.length)](p.title, city.name)
  const climate = CLIMATE_NOTES[city.region]
  const regulation = REGULATIONS_BY_DEPT[city.dept] ?? DEFAULT_REGULATION_NOTE
  const deptFact = DEPT_FACTS[city.dept] ?? DEFAULT_DEPT_FACT
  const responseTime = stats ? formatResponseTime(stats.avgResponseHours) : null
  // Grounded per-city FAQ + expert tips override the template when present.
  const faqList = aiContent?.faqItems && aiContent.faqItems.length > 0 ? aiContent.faqItems : p.faq
  const expertTips = aiContent?.expertTips ?? []
  // Population label varies the page text per city — guards against
  // 0% uniqueness across same-problem-different-city programmatic pages.
  const popLabel = city.population
    ? city.population > 100000 ? `grande ville de ${(city.population / 1000).toFixed(0)}k habitants`
    : city.population > 20000 ? `commune de ${(city.population / 1000).toFixed(0)}k habitants`
    : `commune de ${formatFr(city.population)} habitants`
    : `commune du ${city.deptName}`
  const urg = URGENCY_LABELS[p.urgency]
  const serviceLabel = p.serviceSlugs[0] ? SERVICE_LABELS[p.serviceSlugs[0]] : null

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Problèmes', url: 'https://www.afficheservices.fr/problemes' },
        { name: p.title, url: `https://www.afficheservices.fr/problemes/${p.slug}` },
        { name: city.name, url: `https://www.afficheservices.fr/problemes/${p.slug}/${city.name.toLowerCase()}` },
      ]} />
      {faqList.length > 0 && <FAQSchema items={faqList.map((f) => ({ question: f.q, answer: f.a }))} />}
      <CityPlaceSchema
        city={{
          name: city.name,
          postalCode: city.postalCode,
          dept: city.dept,
          deptName: city.deptName,
          region: city.region,
          lat: city.lat,
          lon: city.lon,
        }}
        service={{ name: serviceLabel?.name ?? p.title, slug: p.serviceSlugs[0] ?? 'devis' }}
        canonicalUrl={`https://www.afficheservices.fr/problemes/${p.slug}/${citySlug}`}
        priceBand={stats?.priceBand}
        offerCount={proCount}
        wikidataUrl={wikidataUrl}
        communeImageUrl={communeImageUrl}
      />

      <section className="bg-gradient-to-br from-red-900 via-rose-900 to-slate-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to={`/problemes/${p.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-red-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> {p.title}
          </Link>
          <span className={`mt-4 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${urg.color}`}>
            {urg.label}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">
            {h1}
          </h1>
          <p className="mt-3 max-w-2xl text-base text-red-200">
            {p.shortDesc} {proCount > 0
              ? `${proCount} artisans disponibles dans le ${city.deptName} (${city.dept}).`
              : `Trouvez un artisan dans le ${city.deptName}.`}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white">
              <Euro className="h-4 w-4" /> {p.avgCost}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-4 py-2 text-sm text-white">
              <MapPin className="h-4 w-4" /> {city.name} ({city.postalCode})
            </span>
            {proCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-xl bg-white/10 px-4 py-2 text-sm text-white">
                <Users className="h-4 w-4" /> {proCount} artisans
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 lg:grid-cols-3 lg:px-6">
        <div className="space-y-10 lg:col-span-2">
          <section>
            <h2 className="text-xl font-bold text-slate-900">{p.title} à {city.name} : que faire ?</h2>
            {aiContent?.answerBlock && (
              <div className="mt-3 rounded-xl border-l-4 border-primary bg-slate-50 p-4 text-sm leading-relaxed text-slate-800">
                {aiContent.answerBlock}
              </div>
            )}
            {/* Phase-2 unique per-city prose replaces the rotated template intro
                when present; otherwise the real-data-enriched template body. */}
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              {aiContent?.localContext ?? introText}
            </p>
            {!aiContent?.localContext && (
              <p className="mt-3 text-sm leading-relaxed text-slate-700">
                À {city.name} — {popLabel} située dans le {city.deptName} ({city.dept}), région {city.region} —
                {proCount > 0
                  ? ` ${proCount} ${serviceLabel?.artisan ?? 'artisans'}s sont référencés et vérifiés sur AfficheServices. `
                  : ` les artisans ${serviceLabel?.artisan ?? ''} qualifiés interviennent dans le ${city.dept} sous 24-48h. `}
                Code postal {city.postalCode}.
              </p>
            )}
            {climate && (
              <p className="mt-3 text-sm leading-relaxed text-slate-700">
                <strong>Contexte régional :</strong> {climate}
              </p>
            )}
          </section>

          {stats && stats.recentLeads > 0 && (
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-sm font-semibold text-emerald-900">
                📈 Activité {city.deptName} ({city.dept}) sur 30 jours
              </p>
              <p className="mt-1 text-sm text-emerald-800">
                <strong>{formatFr(stats.recentLeads)} demandes</strong>
                {responseTime ? ` — réponse moyenne ${responseTime}` : ''}
                {stats.proCount > 0 ? ` · ${stats.proCount} artisans actifs` : ''}.
              </p>
            </section>
          )}

          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-900">
              ⚖️ Réglementation locale ({city.dept})
            </p>
            <p className="mt-1 text-sm leading-relaxed text-amber-800">{regulation}</p>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">
              📊 Le saviez-vous ? ({city.deptName})
            </p>
            <p className="mt-1 text-sm leading-relaxed text-slate-700">{deptFact}</p>
          </section>

          {topPros.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <h2 className="text-lg font-bold text-slate-900">
                Top {topPros.length} {serviceLabel?.artisan ?? 'artisan'}{topPros.length > 1 ? 's' : ''} à {city.name} et {city.deptName}
              </h2>
              <ul className="mt-4 space-y-2">
                {topPros.map((pro, i) => (
                  <li key={pro.id} className="flex items-start gap-3 text-sm">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">{i + 1}</span>
                    <div className="flex-1">
                      <Link to={`/pro/${pro.slug}`} className="font-semibold text-slate-900 hover:text-primary">
                        {pro.companyName}
                      </Link>
                      <span className="ml-2 text-xs text-slate-500">— {pro.city}</span>
                      {Number(pro.reviewCount) > 0 && pro.ratingAvg && (
                        <span className="ml-2 text-xs text-amber-600">★ {pro.ratingAvg} ({pro.reviewCount} avis)</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                Tous certifiés SIRET, intervention dans le {city.deptName} sous 48h en moyenne.
              </p>
            </section>
          )}

          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-emerald-800">
              <CheckCircle className="h-5 w-5" /> Les bons réflexes
            </h2>
            <ol className="mt-4 space-y-2">
              {p.whatToDo.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </section>

          <section className="rounded-2xl border border-red-200 bg-red-50 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-red-800">
              <XCircle className="h-5 w-5" /> À éviter
            </h2>
            <ul className="mt-4 space-y-2">
              {p.dontDo.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" /> {item}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
            <h2 className="text-lg font-bold text-blue-900">Tarif {serviceLabel?.artisan ?? 'artisan'} à {city.name}</h2>
            <p className="mt-2 text-sm text-blue-800">
              Pour {p.title.toLowerCase()} à {city.name}, le coût moyen constaté est de <strong>{p.avgCost}</strong>.
              Ce tarif inclut le déplacement, le diagnostic et l'intervention de base.
              {p.urgency === 'urgent' && ' Un supplément de 30-50 % s\'applique en soirée, week-end et jours fériés.'}
            </p>
          </section>

          {p.aiClaims && p.aiClaims.length > 0 && (
            <section className="rounded-2xl border border-blue-200 bg-blue-50 p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold text-blue-900">
                📊 En chiffres
              </h2>
              <ul className="mt-4 space-y-2">
                {p.aiClaims.map((claim, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-blue-900">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                    {claim}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {p.serviceSlugs[0] && (
            <CrossLinksHub
              primarySlug={p.serviceSlugs[0]}
              cityName={city.name}
              citySlug={citySlug}
              currentRoute="problemes"
            />
          )}

          {neighbors.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-900">{p.title} dans les communes voisines</h2>
              <p className="mt-2 text-sm text-slate-600">
                Les artisans interviennent aussi dans un rayon de 30 km autour de {city.name} :
              </p>
              <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {neighbors.map((n) => (
                  <li key={n.postalCode + n.name}>
                    <Link
                      to={`/problemes/${p.slug}/${n.name.toLowerCase().replace(/['\s]/g, '-')}`}
                      className="block rounded-lg border border-slate-200 bg-white p-2.5 text-sm hover:border-primary hover:bg-slate-50"
                    >
                      <span className="font-medium text-slate-900">{n.name}</span>
                      <span className="ml-1 text-xs text-slate-500">({n.km.toFixed(0)} km)</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {expertTips.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-900">Conseils d'expert à {city.name}</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {expertTips.map((tip, i) => (
                  <div key={i} className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                    <p className="text-sm font-semibold text-emerald-900">{tip.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{tip.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {faqList.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-slate-900">Questions fréquentes</h2>
              <div className="mt-4 space-y-3">
                {faqList.map((item, i) => (
                  <details key={i} className="group rounded-xl border border-border bg-white p-4">
                    <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                      {item.q}
                      <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside>
          <div className="sticky top-20 space-y-4">
            <CityMap lat={Number(city.lat)} lon={Number(city.lon)} cityName={city.name} />
            <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
              <h3 className="text-sm font-bold text-slate-900">
                {serviceLabel?.artisan ? `Trouver un ${serviceLabel.artisan}` : 'Trouver un artisan'} à {city.name}
              </h3>
              <p className="mt-1 text-xs text-slate-500">3 devis gratuits · Réponse sous 48h</p>
              <Link
                to="/devis-gratuit"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover"
              >
                Devis gratuit <ArrowRight className="h-4 w-4" />
              </Link>
              {p.urgency === 'urgent' && (
                <Link
                  to="/urgence"
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100"
                >
                  <Phone className="h-4 w-4" /> Urgences 24h
                </Link>
              )}
            </div>

            {p.serviceSlugs.length > 0 && (
              <div className="rounded-2xl border border-border bg-slate-50 p-5">
                <p className="text-xs font-semibold text-slate-600">Services à {city.name}</p>
                <ul className="mt-3 space-y-2">
                  {p.serviceSlugs.map((slug) => {
                    const svc = SERVICE_LABELS[slug]
                    return svc ? (
                      <li key={slug}>
                        <Link to={`/services/${slug}`} className="flex items-center justify-between text-sm text-slate-700 hover:text-primary">
                          {svc.name} <ArrowRight className="h-3 w-3" />
                        </Link>
                      </li>
                    ) : null
                  })}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  )
}
